/**
 * The canvas element's user-facing noun.
 *
 * This is deliberately separate from `data-pb-editable`: editable type names
 * the editor capability, while kind names what the user sees. Classification
 * runs on the parent's detached DOM, so it must stay pure and must never use
 * layout or getComputedStyle(). The stamp is temporary and is removed before
 * persistence/export by `stripTempAttrs`.
 *
 * Do not merge this with `media-kind.js`. That module classifies picker assets;
 * this one classifies elements already placed on the canvas.
 */

export const KIND_ATTR = 'data-pb-kind';

export const KIND = Object.freeze({
  PHOTO: 'photo',
  LOGO: 'logo',
  ICON: 'icon',
  DECORATION: 'decoration',
  BACKGROUND: 'background',
  HEADING: 'heading',
  PARAGRAPH: 'paragraph',
  LIST_ITEM: 'list-item',
  LINK: 'link',
  TEXT: 'text',
  BUTTON: 'button',
  CONTAINER: 'container',
  EMBED: 'embed',
  MEDIA: 'media',
  FIELD: 'field',
  UNKNOWN: 'unknown'
});

export const KIND_LABELS = Object.freeze({
  [KIND.PHOTO]: 'Photo',
  [KIND.LOGO]: 'Logo',
  [KIND.ICON]: 'Icon',
  [KIND.DECORATION]: 'Decoration',
  [KIND.BACKGROUND]: 'Background',
  [KIND.HEADING]: 'Heading',
  [KIND.PARAGRAPH]: 'Paragraph',
  [KIND.LIST_ITEM]: 'List Item',
  [KIND.LINK]: 'Link',
  [KIND.TEXT]: 'Text',
  [KIND.BUTTON]: 'Button',
  [KIND.CONTAINER]: 'Container',
  [KIND.EMBED]: 'Embed',
  [KIND.MEDIA]: 'Media',
  [KIND.FIELD]: 'Field',
  [KIND.UNKNOWN]: 'Element'
});

export const DECORATIVE_CLASSES = Object.freeze([
  'cs-graphic',
  'cs-decorative-svg',
  'cs-wave',
  'cs-waves',
  'cs-divider',
  'cs-divider-line',
  'cs-shape',
  'cs-bg',
  'cs-mask',
  'cs-blob',
  'cs-pattern',
  'cs-overlay',
  'pb-decorative-svg'
]);

export const DECORATIVE_SELECTOR = [
  '[data-pb-decorative="true"]',
  '[class*="cs-graphic"]',
  ...DECORATIVE_CLASSES.map((className) => `.${className}`)
].join(',');

export const ICON_CLASSES = Object.freeze([
  'cs-icon',
  'cs-li-icon',
  'cs-box-icon',
  'cs-social-icon',
  'cs-link-icon',
  'cs-contact-icon',
  'cs-drop-icon',
  'cs-button-arrow',
  'icon'
]);

const ICON_SELECTOR = ICON_CLASSES.map((className) => `.${className}`).join(',');

function tagOf(el) {
  return String(el?.tagName || '').toUpperCase();
}

function hasTextBearingGraphicHost(el) {
  const host = el?.closest?.('a, button, li');
  if (!host) return false;
  const text = String(host.textContent || '')
    .replace(/\s+/g, ' ')
    .trim();
  return !!text;
}

function isIconStructure(el) {
  if (!el) return false;
  if (tagOf(el) === 'I') return true;
  if (el.matches?.(ICON_SELECTOR)) return true;
  if (el.closest?.(ICON_SELECTOR)) return true;
  return hasTextBearingGraphicHost(el);
}

function isBackgroundElement(el, editableType) {
  if (editableType === 'css-background') return true;
  const tag = tagOf(el);
  if (tag === 'PICTURE' && el.classList?.contains('cs-background')) return true;
  return tag === 'IMG' && !!el.closest?.('picture.cs-background, .cs-background');
}

export function kindLabel(kind, { tagName } = {}) {
  if (KIND_LABELS[kind]) return KIND_LABELS[kind];
  const tag = String(tagName || '').toUpperCase();
  if (/^H[1-6]$/.test(tag)) return KIND_LABELS[KIND.HEADING];
  if (tag === 'P') return KIND_LABELS[KIND.PARAGRAPH];
  if (tag === 'LI') return KIND_LABELS[KIND.LIST_ITEM];
  if (tag === 'A') return KIND_LABELS[KIND.LINK];
  return KIND_LABELS[KIND.UNKNOWN];
}

/** Ordered classifier. The rule order is part of the public contract. */
export function classifyElementKind(el, { editableType } = {}) {
  const type = String(editableType || el?.getAttribute?.('data-pb-editable') || '');
  const tag = tagOf(el);

  if (type === 'logo') return KIND.LOGO;
  if (isBackgroundElement(el, type)) return KIND.BACKGROUND;
  if (['SVG', 'IMG', 'I'].includes(tag) && isIconStructure(el)) return KIND.ICON;
  if (el?.matches?.(DECORATIVE_SELECTOR)) return KIND.DECORATION;
  if (type === 'image' || tag === 'PICTURE') return KIND.PHOTO;

  if (type === 'text') {
    if (/^H[1-6]$/.test(tag)) return KIND.HEADING;
    if (tag === 'P') return KIND.PARAGRAPH;
    if (tag === 'LI') return KIND.LIST_ITEM;
    if (tag === 'A') return KIND.LINK;
    return KIND.TEXT;
  }

  if (type && Object.values(KIND).includes(type)) return type;
  return KIND.UNKNOWN;
}

export function kindOf(el) {
  return el?.getAttribute?.(KIND_ATTR) || KIND.UNKNOWN;
}

export function stampElementKinds(root) {
  if (!root?.querySelectorAll) return root;
  const candidates = new Set();
  if (root.matches?.(`[data-pb-editable],${DECORATIVE_SELECTOR}`)) candidates.add(root);
  for (const el of root.querySelectorAll(`[data-pb-editable],${DECORATIVE_SELECTOR}`)) {
    candidates.add(el);
  }
  for (const el of candidates) {
    el.setAttribute(
      KIND_ATTR,
      classifyElementKind(el, { editableType: el.getAttribute('data-pb-editable') || '' })
    );
  }
  return root;
}
